<template>
  <fragment>
    <header class="action-container">
      <v-row>
        <v-col cols="12" md="6" lg="5" xl="4" class="order-2 order-md-1">
          <wx-text-field
            v-model="searchKeyword"
            :label="$t('common.search')"
            :change="checkFilterResult()"
            prepend-inner-icon="mdi-magnify"
            hide-details
            single-line
            clearable
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="7"
          xl="8"
          class="action-buttons order-1 order-md-2 d-flex flex-wrap justify-center justify-md-end align-md-end"
        >
          <!-- 
            Export/Import Reason Tree - - - - - - - - - - - - - - -
            -->
          <wx-btn-standard v-if="activeFactory && isWorximityAdmin" @click="exportReasonTree()" class="mx-1 mb-2 mb-md-1">
            <v-icon left>mdi-cloud-download-outline</v-icon>
            {{ $t("downtimeReason.export.button") }}
          </wx-btn-standard>
          <dialog-file-upload
            v-if="activeFactory && isWorximityAdmin"
            :button-title="$t('downtimeReason.import.button')"
            :dialog-title="$t('downtimeReason.import.title')"
            :factory-warning="factoryWarning"
            :upload-instruction-texts="instructions"
            :upload-warning="warning"
            :template-available="false"
            @selectedFile="importReasonTree"
            activator-btn-class="mb-2 mb-sm-1 mx-2"
          />

          <!--
            Create Reason - - - - - - - - - - - - - - -
            -->
          <wx-btn-standard :to="getReasonCreationUrl()" :disabled="!enableNewReasonButton" class="mx-1 mb-2 mb-md-1">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("downtimeReason.wizard.reasonCreation.buttonNew") }}
          </wx-btn-standard>

          <!-- 
            Create Category - - - - - - - - - - - -
            -->
          <wx-btn-standard
            :to="getCategoryCreationUrl()"
            :disabled="!enableNewCategoryButton"
            class="mx-1 mb-2 mb-md-1"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t("downtimeReason.wizard.categoryCreation.buttonNew") }}
          </wx-btn-standard>

          <!-- 
            Edit - - - - - - - - - - - - - - -
            -->
          <wx-btn-standard
            :to="getEditionUrl()"
            :disabled="!enableEditReasonButton && !enableEditCategoryButton"
            class="mx-1 mb-2 mb-md-1"
          >
            <v-icon left>mdi-pencil</v-icon>
            {{ $t("downtimeReason.wizard.reasonEdition.buttonNew") }}
          </wx-btn-standard>

          <!-- 
            Delete - - - - - - - - - - - - -
            -->
          <dialog-confirm-deletion
            :deletion-title="getDeletionTitle()"
            :info-box-text-title="$t('common.attention')"
            :info-box-text="getDeletionInfoText()"
            :confirm-text-prefix="getDeletionConfirmationPrefix()"
            :element-display-name="getObjectToDeleteDisplayName()"
            :disabled="!enableDeleteButton"
            :delete-button-text="getDeleteButtonText()"
            @confirm="deleteSelected()"
            activator-btn-class="mx-1 mb-2 mb-md-1"
          />
        </v-col>
      </v-row>
    </header>

    <v-container>
      <v-alert type="error" prominent v-model="importErrorOccurred">
        <p class="info-box-text">{{ $t("common.attention") }}</p>
        <ul class="errors-list">
          <li v-for="(value, index) in importErrors" :key="index">{{ value }}</li>
        </ul>
        <v-btn
          @click="
            alert = false;
            importErrorOccurred = false;
            importErrors = [];
          "
          :title="$t('common.close')"
          class="close-btn"
          icon
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-alert>
    </v-container>

    <div class="finder-container">
      <finder
        ref="myFinder"
        :tree="tree"
        :theme="finderTheme"
        :item-component="itemComponent"
        :arrow-component="arrowComponent"
        :filter="(item) => this.filterTree(item)"
        :class="$vuetify.theme.dark ? 'theme--dark' : 'theme--light'"
        @expand="onExpand"
      >
      </finder>
      <wx-alert-query-message
        v-if="noResult"
        :text="$t('common.noMatchingRecords')"
        container-css-class="pa-4 rounded-lg"
        no-results-icon
        bordered
      />
    </div>
  </fragment>
</template>

<script>
import { Finder } from "@jledentu/vue-finder";
import { mapActions, mapGetters } from "vuex";
import "@jledentu/vue-finder/dist/vue-finder.css";
import WxAlertQueryMessage from "@/components/ui/WxAlertQueryMessage";
import WxBtnStandard from "@/components/ui/WxBtnStandard";
import WxTextField from "@/components/ui/WxTextField";
import DialogConfirmDeletion from "@/components/DialogConfirmDeletion";
import DowntimeReasonService from "@/components/downtimereason/DowntimeReasonService";
import ErrorHandling from "@/components/ErrorHandling";
import RouteService from "@/router/RouteService";
import StyleValues from "@/styles/_values.scss";
import DialogFileUpload from "@/components/DialogFileUpload.vue";

export default {
  name: "DowntimeReasons",
  components: {
    DialogFileUpload,
    Finder,
    WxAlertQueryMessage,
    WxBtnStandard,
    WxTextField,
    DialogConfirmDeletion,
  },
  data() {
    return {
      noResult: false,
      searchKeyword: "",
      tree: {
        id: "root",
        children: [],
      },
      categoryById: new Map(),

      selectedElement: {},
      selectedElementParent: {},
      selectedElementChain: [],

      enableNewReasonButton: false,
      enableNewCategoryButton: false,
      enableDeleteButton: false,
      enableEditReasonButton: false,
      enableEditCategoryButton: false,

      itemComponent: {
        props: ["item"],
        template:
          "<div>" +
          " <v-icon>{{ (item.type === 'category' ) ? 'mdi-folder-outline' : ((item.planned ? '$plannedReasonIcon' : '$unplannedReasonIcon' )) }}</v-icon>" +
          " {{ item.code }}" +
          " - " +
          "{{ item.name }}" +
          "</div>",
      },
      arrowComponent: {
        props: ["expanded", "item", "theme"],
        template: "<v-icon>mdi-menu-right</v-icon>", // eslint-disable-line @intlify/vue-i18n/no-raw-text
      },
      exportInProgress: false,
      importErrorOccurred: false,
      importErrors: [],
    };
  },
  computed: {
    ...mapGetters("navigation", ["activeFactory", "activeFactoryProductionUnits"]),
    ...mapGetters("user", ["theme", "isWorximityAdmin"]),
    finderTheme() {
      return {
        primaryColor: this.theme === "light" ? StyleValues.primaryLight : StyleValues.primaryDark,
        arrowColor: this.theme === "light" ? StyleValues.color_text_themeLight : StyleValues.color_text_themeDark,
        separatorColor:
          this.theme === "light" ? StyleValues.color_border_themeLight : StyleValues.color_border_themeDark,
        separatorWidth: "1px",
      };
    },
    factoryName() {
      return this.activeFactory.name;
    },
    factoryWarning() {
      let label = this.$t("common.importOnFactory");
      return `<span>${label}</span><span class="font-weight-bold">${this.factoryName}</span><span>.</span>`;
    },
    instructions() {
      return [this.$t("downtimeReason.import.instruction"), this.$t("downtimeReason.import.instruction2")];
    },
    warning() {
      return this.$t("downtimeReason.import.warning");
    },
  },
  watch: {
    activeFactory() {
      this.searchKeyword = "";
      this.tree = {
        id: "root",
        children: [],
      };
      this.categoryById = new Map();
      this.selectedElement = {};
      this.selectedElementParent = {};
      this.selectedElementChain = [];
      this.importErrorOccurred = false;
      this.importErrors = [];
      this.loadTree();
    },
  },
  methods: {
    ...mapActions("dashboard", ["fetchAvailableDowntimeReasons"]),
    ...mapActions("operation", ["showOperationSuccess", "showOperationError"]),
    checkFilterResult() {
      this.$nextTick(function() {
        this.noResult = this.$refs.myFinder.treeModel.filtered.length <= 1;
        if (this.noResult) {
          // Nothing matches the search criteria, hide the buttons
          this.enableNewReasonButton = false;
          this.enableNewCategoryButton = false;
          this.enableDeleteButton = false;
          this.enableEditReasonButton = false;
          this.enableEditCategoryButton = false;
        }
        if (this.selectedElement.id) {
          if (this.selectedElement.level === 1) {
            // Root category. Disable the Edit & Deletion buttons
            this.enableDeleteButton = false;
            this.enableEditCategoryButton = false;
          } else {
            // Child category. Enable the Edit & Deletion buttons
            this.enableDeleteButton = true;
            this.enableEditCategoryButton = true;
            const selectedElementIsFilteredIn = this.$refs.myFinder.treeModel.filtered.includes(
              this.selectedElement.id,
            );
            if (!selectedElementIsFilteredIn) {
              // The current selected element does not match the search criteria: unselect it; expand only the first column ("root")
              this.selectedElement = {};
              this.selectedElementParent = {};
              this.selectedElementChain = [];
              this.$refs.myFinder.expand("root");
            }
          }
        }
      });
    },
    loadTree() {
      if (this.activeFactory) {
        return DowntimeReasonService.getDowntimeReasonTree(this.activeFactory.id)
          .then((response) => this.handleDowntimeReasonTreeRetrievalResponse(response))
          .catch((error) => this.handleDowntimeReasonTreeRetrievalError(error.response));
      }
    },
    handleDowntimeReasonTreeRetrievalResponse(httpResponse) {
      if (httpResponse.status === 200) {
        this.tree = {
          id: httpResponse.data.id,
          type: "category",
          level: 0,
          children: httpResponse.data.children,
        };
        let categoryMap = new Map();
        this.buildCategoryById(httpResponse.data.children, categoryMap);
        this.categoryById = categoryMap;
      } else {
        this.handleDowntimeReasonTreeRetrievalError(httpResponse);
      }
    },

    buildCategoryById(children, destinationMap) {
      children.forEach((element) => {
        if (element.type === "category") {
          destinationMap[element.id] = { id: element.id, code: element.code, name: element.name };
          if (element.children.length > 0) {
            this.buildCategoryById(element.children, destinationMap);
          }
        }
      });
    },

    handleDowntimeReasonTreeRetrievalError(httpResponse) {
      this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
    },

    getErrorMessage(code, args) {
      if (code === "CS_DWNR_DELETE_20008") {
        return this.$t("downtimeReason.errors.downtimeReasonUsedInShift", {
          reasonCode: args.reason_code,
          reasonName: args.reason_name,
          workShiftNames: args.work_shift_names,
        });
      }
      return this.$t("common.errors.default", { code: code });
    },

    onExpand({ expanded }) {
      this.enableNewReasonButton = false;
      this.enableNewCategoryButton = false;
      this.enableDeleteButton = false;
      this.enableEditReasonButton = false;
      this.enableEditCategoryButton = false;
      this.selectedElementChain = expanded.filter((el) => el !== "root").map((el) => this.searchTree(this.tree, el));
      if (this.selectedElementChain.length > 0) {
        this.selectedElement = this.selectedElementChain[this.selectedElementChain.length - 1];
        this.$router.replace({ query: { expand: this.selectedElement.id } });
        if (this.selectedElementChain.length >= 2) {
          this.selectedElementParent = this.selectedElementChain[this.selectedElementChain.length - 2];
        }
        if (this.selectedElement) {
          if (this.selectedElementChain.length === 1) {
            // Root category. Disable the Edit & Deletion buttons
            this.enableDeleteButton = false;
            this.enableEditCategoryButton = false;
            this.enableNewCategoryButton = true;
            this.enableNewReasonButton = true;
          } else {
            this.enableDeleteButton = true;

            if (this.selectedElement.type === "category") {
              // Allowed to create a new reason under the selected category
              this.enableNewReasonButton = true;
              this.enableNewCategoryButton = true;
              this.enableEditCategoryButton = true;
            } else {
              this.enableEditReasonButton = true;
            }
          }
        }
      }
    },

    searchTree(element, matchingId) {
      if (element.id === matchingId) {
        return element;
      } else if (element.children != null) {
        let i;
        let result = null;
        for (i = 0; result == null && i < element.children.length; i++) {
          result = this.searchTree(element.children[i], matchingId);
        }
        return result;
      }
      return null;
    },

    filterTree(treeItem) {
      if (this.searchKeyword && this.searchKeyword.length > 0 && (treeItem.code || treeItem.name)) {
        const codeWithName = treeItem.code.toLowerCase() + " - " + treeItem.name.toLowerCase();
        return codeWithName.includes(this.searchKeyword);
      }
      return true;
    },

    getDeletionTitle() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          return this.$t("downtimeReason.wizard.categoryDeletion.title");
        } else {
          return this.$t("downtimeReason.wizard.reasonDeletion.title");
        }
      }
      return "";
    },
    getDeleteButtonText() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          return this.$t("downtimeReason.wizard.categoryDeletion.deleteDowntimeCategoryBtn");
        } else {
          return this.$t("downtimeReason.wizard.reasonDeletion.deleteDowntimeReasonBtn");
        }
      }
      return "";
    },

    getDeletionInfoText() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          let counters = this.getSubCategoryAndReasonCounters(this.selectedElement);
          return this.$t("downtimeReason.wizard.categoryDeletion.warning", {
            subCategoryCount: counters.category,
            reasonCount: counters.reason,
          });
        } else {
          return this.$t("downtimeReason.wizard.reasonDeletion.warning");
        }
      }
      return "";
    },

    getSubCategoryAndReasonCounters(fromCategory) {
      let counters = { category: 0, reason: 0 };
      if (fromCategory.children) {
        let i;
        for (i = 0; i < fromCategory.children.length; i++) {
          let element = fromCategory.children[i];
          if (element.type === "category") {
            counters.category++;

            let thisSubCategoryCounters = this.getSubCategoryAndReasonCounters(element);
            counters.category += thisSubCategoryCounters.category;
            counters.reason += thisSubCategoryCounters.reason;
          }
          if (element.type === "reason") {
            counters.reason++;
          }
        }
      }
      return counters;
    },

    getDeletionConfirmationPrefix() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          return this.$t("downtimeReason.wizard.categoryDeletion.confirmationPrefix");
        } else {
          return this.$t("downtimeReason.wizard.reasonDeletion.confirmationPrefix");
        }
      }
      return "";
    },

    getObjectToDeleteDisplayName() {
      if (this.selectedElement) {
        return this.selectedElement.code + " - " + this.selectedElement.name;
      }
      return "";
    },

    deleteSelected() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          this.deleteCategory(this.selectedElement);
        } else {
          this.deleteReason(this.selectedElement);
        }
      }
      return "";
    },

    deleteCategory(category) {
      DowntimeReasonService.deleteCategory(category.id)
        .then((response) => this.handleCategoryDeletionResponse(response, category))
        .catch((error) => this.handleDeletionError(error.response));
    },

    handleCategoryDeletionResponse(httpResponse, category) {
      if (httpResponse.status === 204) {
        this.fetchAvailableDowntimeReasons({});
        const successMessage = this.$t("downtimeReason.successfullyCategoryDeleted", {
          name: category.code + " - " + category.name,
        });
        const parentCategoryId = this.selectedElementParent.id;
        this.onSuccessfulEditionOperation(successMessage, parentCategoryId);
      } else {
        this.handleDeletionError(httpResponse);
      }
    },

    deleteReason(reason) {
      DowntimeReasonService.deleteReason(reason.id)
        .then((response) => this.handleReasonDeletionResponse(response, reason))
        .catch((error) => this.handleDeletionError(error.response));
    },

    handleReasonDeletionResponse(httpResponse, reason) {
      if (httpResponse.status === 204) {
        this.fetchAvailableDowntimeReasons({});
        const successMessage = this.$t("downtimeReason.successfullyReasonDeleted", {
          name: reason.code + " - " + reason.name,
        });
        const parentCategoryId = this.selectedElementParent.id;
        this.onSuccessfulEditionOperation(successMessage, parentCategoryId);
      } else {
        this.handleDeletionError(httpResponse);
      }
    },

    handleDeletionError(httpResponse) {
      this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
    },

    onSuccessfulEditionOperation(successMessage, treeElementIdToExpand) {
      this.loadTree().then(() => {
        this.showOperationSuccess(successMessage);
        this.$refs.myFinder.expand(treeElementIdToExpand);
      });
    },

    getReasonCreationUrl() {
      if (this.selectedElement && this.selectedElement.type === "category") {
        return RouteService.toDowntimeReasonsCreation(this.selectedElement.id);
      }
      return RouteService.toHome();
    },
    getCategoryCreationUrl() {
      if (this.selectedElement) {
        return RouteService.toDowntimeCategoryCreation(this.selectedElement.id);
      }
      return RouteService.toHome();
    },
    getEditionUrl() {
      if (this.selectedElement) {
        if (this.selectedElement.type === "category") {
          return this.getCategoryEditionUrl();
        }
        if (this.selectedElement.type === "reason") {
          return this.getReasonEditionUrl();
        }
      }
      return RouteService.toHome();
    },
    getReasonEditionUrl() {
      if (this.selectedElement) {
        return RouteService.toDowntimeReasonEdition(this.selectedElement.id);
      }
      return RouteService.toHome();
    },
    getCategoryEditionUrl() {
      if (this.selectedElement) {
        return RouteService.toDowntimeCategoryEdition(this.selectedElement.id);
      }
      return RouteService.toHome();
    },
    exportReasonTree() {
      this.exportInProgress = true;
      DowntimeReasonService.downloadReasonTree(this.activeFactory.id)
        .then((httpResponse) => this.handleExportResponse(httpResponse))
        .catch((httpResponse) => this.handleExportError(httpResponse));
    },
    handleExportResponse(httpResponse) {
      let file = window.URL.createObjectURL(new Blob([httpResponse.data]));
      let docUrl = document.createElement("a");
      docUrl.href = file;
      docUrl.setAttribute("download", "downtime-reason-tree-export.xlsx");
      document.body.appendChild(docUrl);
      docUrl.click();
      document.body.removeChild(docUrl);
      this.exportInProgress = false;
    },
    handleExportError(httpResponse) {
      this.exportInProgress = false;
      this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
    },
    importReasonTree(file) {
      if (this.activeFactory) {
        DowntimeReasonService.uploadReasonTree(this.activeFactory.id, file)
          .then((response) => this.handleUploadResponse(response))
          .catch((error) => this.handleUploadError(error.response));
      }
    },
    handleUploadResponse(httpResponse) {
      if (httpResponse.status === 201) {
        this.importErrorOccurred = false;
        this.importErrors = [];
        this.showOperationSuccess(this.$t("product.successfullyUploaded"));
        this.loadTree();
      }
    },
    handleUploadError(httpResponse) {
      let errors = httpResponse.data.errors.map((e) => e.message);
      if (errors && errors.length > 0) {
        let maximumNumberOfErrorsToDisplay = 10;
        if (errors.length > maximumNumberOfErrorsToDisplay) {
          this.importErrors = errors.slice(0, maximumNumberOfErrorsToDisplay);
        } else {
          this.importErrors = errors;
        }
        this.importErrorOccurred = true;
      } else {
        this.showOperationError(ErrorHandling.buildErrorsMessages(httpResponse, this.getErrorMessage));
      }
    },
  },
  mounted() {
    if (this.activeFactory) {
      this.loadTree().then(() => {
        if (this.$route.query.expand) {
          this.$refs.myFinder.expand(this.$route.query.expand);
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.tree-container {
  font-size: var(--font-size-sm);
  border-radius: var(--border-radius-lg);
  background-color: var(--color-element-layer1);

  &:empty {
    display: none;
  }

  ::v-deep .item:not(.expanded):hover {
    background-color: var(--color-control-background-theme);
  }

  ::v-deep .list-container {
    height: auto;
  }

  ::v-deep .inner-item {
    display: flex;
    align-items: center;

    .v-icon {
      margin-right: 8px;
    }
  }

  ::v-deep .item.expanded {
    // items within primary color
    .v-icon {
      color: White;
    }
  }
}

.action-container {
  display: flex;
  margin-bottom: 20px;

  .v-input {
    margin-right: auto;
  }
}
</style>
